import React, { useCallback, useEffect, useRef } from 'react';
import { usePrevious, useWindowSize } from 'react-use';

import { createStyles, Theme, withStyles, WithStyles } from '@material-ui/core';
import classnames from 'classnames';
import { map } from 'lodash';

import { useLumaBotContext } from '../LumaBot.context';
import { EMessageType } from '../WIP/ui-model/types';

import Avatar from './Avatar';
import Message from './Message';
import MessageWrapper, { MessageWrapperProps, TMessageWrapper } from './MessageWrapper';

const styles = (theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      flexDirection: 'column',
      position: 'relative',
      overflowY: 'auto',
      padding: theme.spacing.unit * 1.5,
      marginBottom: theme.spacing.unit * 1.5,
    },
    rootBlocked: {
      overflowY: 'hidden',
    },
    gap: {
      flex: 1,
    },
  });

export interface MessagesProps
  extends WithStyles<typeof styles>,
    Omit<React.HTMLAttributes<HTMLDivElement>, 'onSubmit'> {
  messages: MessageWrapperProps['message'][];
  avatarInbound?: MessageWrapperProps['avatarInbound'];
  onSubmit: MessageWrapperProps['onSubmit'];
  onRevert: (message: TMessageWrapper) => void;
}

const Messages: React.FC<MessagesProps> = ({ classes, className, messages, avatarInbound, onSubmit, onRevert }) => {
  const { mode, typing, isLiveChatEnabled } = useLumaBotContext();
  const prevMode = usePrevious(mode);
  const ref = useRef<HTMLDivElement | null>(null);
  const scrollHeight = useRef(0);
  const { height } = useWindowSize();

  // Scroll to the bottom in case `window.innerHeight` changes. E.g.:
  // - Keyboard on Android devices
  // - Close/open the chat
  useEffect(() => {
    if (ref.current) ref.current.scroll({ top: ref.current.scrollHeight });
  }, [height]);

  // Scroll to the bottom when the chat is unblocked. E.g.:
  // - Scheduler message changing from `more details` mode to the chat mode
  useEffect(() => {
    if (ref.current && mode !== 'blocked' && prevMode === 'blocked') {
      setTimeout(() => ref.current!.scroll({ top: ref.current!.scrollHeight }));
    }
  }, [mode, prevMode]);

  const handleNewRef = useCallback(
    (node: HTMLDivElement | null) => {
      ref.current = node;

      if (!node || mode === 'blocked') return;

      // Scroll to the bottom each time there's a new message
      if (node.scrollHeight !== scrollHeight.current) {
        node.scroll({ top: node.scrollHeight, behavior: 'smooth' });
        scrollHeight.current = node.scrollHeight;
      }
    },
    [mode],
  );

  return (
    <div
      ref={(newRef) => handleNewRef(newRef)}
      className={classnames(classes.root, { [classes.rootBlocked]: mode === 'blocked' }, className)}
    >
      <div className={classes.gap} />
      {map(messages, (message) => (
        <MessageWrapper
          key={`${message.type}@${message.createdAt}@${message.text}`}
          message={message}
          avatarInbound={message.user ? <Avatar size="xxsmall" user={message.user} /> : avatarInbound}
          onSubmit={onSubmit}
          includeRevert={message.metadata.includeRevert}
          includeTimestamp={message.type === EMessageType.Outbound && message.metadata.isLastMessage}
          onRevert={() => onRevert(message)}
        />
      ))}
      {typing && (
        <Message
          variant="typing"
          avatarInbound={isLiveChatEnabled ? <Avatar size="xxsmall" user="force" /> : avatarInbound}
        />
      )}
    </div>
  );
};

export default withStyles(styles, { name: 'Messages' })(Messages);
